import type { ReactNode } from 'react';
import type { Paged } from '../lib/api';
import {
  IconAlert, IconChevronLeft, IconChevronRight, IconChevronsLeft, IconChevronsRight, IconInbox,
} from './Icons';

/**
 * The table every list screen uses.
 *
 * The old app loaded whole tables and let DataTables page them in the browser, which is why
 * some screens crawl. This pages on the server, and every instance gets a page-size selector
 * as standard rather than as an option each screen has to remember to add.
 */

export const PAGE_SIZES = [5, 10, 25, 50, 100, 500, 1000] as const;

const PAGE_SIZE_KEY = 'ccms.pageSize';

/**
 * How many rows to show, remembered across reloads and shared by every table.
 *
 * Rows-per-page is a statement about the person and their screen, not about the screen they
 * happen to be on - someone who wants 10 rows on a laptop wants 10 rows everywhere, and
 * having to set it again on each list and after every refresh is just friction.
 *
 * Anything not in PAGE_SIZES is ignored rather than trusted: the value is user-writable and
 * feeds a query string.
 */
export function storedPageSize(): number {
  try {
    const raw = Number(localStorage.getItem(PAGE_SIZE_KEY));
    if ((PAGE_SIZES as readonly number[]).includes(raw)) return raw;
  } catch {
    // Private mode or a blocked store. Fall through to the default.
  }

  return PAGE_SIZES[2];
}

export function rememberPageSize(size: number): void {
  try {
    localStorage.setItem(PAGE_SIZE_KEY, String(size));
  } catch {
    // Not being able to remember the choice must never break changing it.
  }
}

export type Column<T> = {
  key: string;
  header: string;
  /** Cell renderer. Given the row, returns whatever should be shown. */
  render: (row: T) => ReactNode;
  /** Right-aligned for numbers and money, so digits line up. */
  align?: 'left' | 'right' | 'center';
  width?: string;
  /**
   * Keep the cell on one line. For short identity codes like `STD-1035` the browser will
   * otherwise break at the hyphen when the column is narrow, splitting them across two rows.
   */
  nowrap?: boolean;
};

type Props<T> = {
  columns: Column<T>[];
  data: Paged<T> | null;
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  rowKey: (row: T) => string | number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** Optional toolbar content: search boxes, action buttons. */
  toolbar?: ReactNode;
  /** Rendered above the toolbar, inside the table's frame. Used for the filter bar. */
  above?: ReactNode;
};

/**
 * Placeholder rows while the first page loads.
 *
 * A spinner in the middle of an empty table collapses the layout and then snaps it back;
 * shaped rows keep the height stable so the page does not jump under the cursor.
 */
function SkeletonRows<T>({ columns, rows = 6 }: { columns: Column<T>[]; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r}>
          {columns.map((column, c) => (
            <td key={column.key}>
              <div
                className="skel"
                style={{
                  height: 11,
                  // Varied widths so it reads as text rather than a progress bar.
                  width: `${[68, 46, 58, 38, 52][(r + c) % 5]}%`,
                  marginLeft: column.align === 'right' ? 'auto' : undefined,
                  marginRight: column.align === 'center' ? 'auto' : undefined,
                  opacity: 1 - r * 0.11,
                }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function DataTable<T>({
  columns,
  data,
  loading = false,
  error = null,
  emptyMessage = 'Nothing to show.',
  rowKey,
  onPageChange,
  onPageSizeChange,
  toolbar,
  above,
}: Props<T>) {
  const page = data?.page ?? 1;
  // Before the first response lands there is no server-reported size, so show the remembered
  // one rather than the default - otherwise the selector flicks from 25 to 10 on every load.
  const pageSize = data?.pageSize ?? storedPageSize();
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const firstRow = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(page * pageSize, totalCount);

  const isEmpty = !loading && !error && (data?.items.length ?? 0) === 0;

  return (
    <div className="dt">
      {above}
      {toolbar && <div className="dt__toolbar">{toolbar}</div>}

      <div className="dt__scroll">
        <table className="dt__table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{
                    width: column.width,
                    textAlign: column.align ?? 'left',
                    whiteSpace: column.nowrap ? 'nowrap' : undefined,
                  }}
                  scope="col"
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading && <SkeletonRows columns={columns} />}

            {!loading && error && (
              <tr>
                <td colSpan={columns.length} className="dt__state dt__state--error">
                  <div className="empty">
                    <div
                      className="empty__icon"
                      style={{ background: 'var(--danger-soft)', color: 'var(--danger-text)' }}
                    >
                      <IconAlert />
                    </div>
                    <div className="empty__title">Could not load this list</div>
                    <div className="empty__body">{error}</div>
                  </div>
                </td>
              </tr>
            )}

            {isEmpty && (
              <tr>
                <td colSpan={columns.length} className="dt__state">
                  <div className="empty">
                    <div className="empty__icon"><IconInbox /></div>
                    <div className="empty__title">Nothing here yet</div>
                    <div className="empty__body">{emptyMessage}</div>
                  </div>
                </td>
              </tr>
            )}

            {!loading &&
              !error &&
              data?.items.map((row) => (
                <tr key={rowKey(row)}>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      style={{
                        textAlign: column.align ?? 'left',
                        whiteSpace: column.nowrap ? 'nowrap' : undefined,
                      }}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="dt__footer">
        <div className="dt__size">
          <label htmlFor="dt-page-size">Rows</label>
          <select
            id="dt-page-size"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div className="dt__count" aria-live="polite">
          {totalCount === 0 ? 'No records' : `${firstRow}–${lastRow} of ${totalCount}`}
        </div>

        <div className="dt__pager">
          <button
            type="button"
            onClick={() => onPageChange(1)}
            disabled={!data?.hasPrevious}
            aria-label="First page"
            title="First page"
          >
            <IconChevronsLeft />
          </button>
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={!data?.hasPrevious}
            aria-label="Previous page"
            title="Previous page"
          >
            <IconChevronLeft />
          </button>

          <span className="dt__pageinfo">
            {totalPages === 0 ? 0 : page} / {totalPages}
          </span>

          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={!data?.hasNext}
            aria-label="Next page"
            title="Next page"
          >
            <IconChevronRight />
          </button>
          <button
            type="button"
            onClick={() => onPageChange(totalPages)}
            disabled={!data?.hasNext}
            aria-label="Last page"
            title="Last page"
          >
            <IconChevronsRight />
          </button>
        </div>
      </div>
    </div>
  );
}
